// src/pages/public/Login.jsx
// FASE 1.B — pixel-perfect del mockup v4 screens_auth.jsx.
// FASE 2.A bug fix — Login detecta sesión activa y redirige al destino correcto:
//   admin → /admin/dashboard
//   resto → /dashboard (ProtectedRoute requireAccess decide /paywall si no hay)
// El useEffect cubre 2 casos: login fresh (user pasa de null a fbUser) y
// usuario que entra a /login con sesión ya activa (cerró tab y volvió).
import { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams, useNavigationType } from 'react-router-dom';
import { loginWithEmail, loginWithGoogle, authErrorMessage } from '../../auth/authHelpers';
import { useAuth } from '../../auth/AuthContext';
import { AuthBack, AuthLogo, Field, GoogleBtn, Divider, ErrorBanner } from './_authShared';
import SplashScreen from '../../components/SplashScreen';

// UX audit ISSUE 7: rutas válidas como destino post-login. Solo respetamos
// `from` si matchea uno de estos prefijos (evita loops y secuestros vía
// state.from='/login' o state.from='/paywall' construido a mano).
// UX audit ISSUE B: agregado `/historial` (deeplink nuevo del historial).
// FIX cliente→/sin-permisos: SAFE_PATHS separado por rol. Si un cliente
// intentó entrar a /admin/dashboard sin sesión, ProtectedRoute setea
// state.from='/admin/dashboard'; al loguearse nos íbamos ahí y la ruta
// admin lo rebotaba a /sin-permisos. Filtrando aquí, el cliente cae en
// destinationFor(userData)='/dashboard' que es lo correcto.
const ADMIN_SAFE_PATHS = /^\/(dashboard|historial|como-funciona|conectar-telegram|admin\/dashboard|gestion)/;
const CLIENT_SAFE_PATHS = /^\/(dashboard|historial|como-funciona|conectar-telegram)/;

function destinationFor(userData) {
  if (!userData) return '/dashboard';
  if (userData.role === 'admin') return '/admin/dashboard';
  return '/dashboard';
}

// UX audit ISSUE 10: banner amarillo cuando el query param indica que la
// sesión expiró. ProtectedRoute setea ?reason=session_expired al detectar
// transición user!=null → null durante navegación.
function SessionExpiredBanner({ onDismiss }) {
  return (
    <div
      role="status"
      style={{
        background: 'rgba(234,179,8,0.12)',
        border: '1px solid rgba(234,179,8,0.35)',
        color: '#92400E',
        padding: '10px 14px',
        borderRadius: 10,
        fontSize: 13,
        marginBottom: 14,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <span style={{ flex: 1 }}>⏱️ Tu sesión expiró. Volvé a iniciar sesión.</span>
      <button
        onClick={onDismiss}
        aria-label="Cerrar aviso"
        style={{
          background: 'none',
          border: 0,
          color: 'inherit',
          fontSize: 16,
          cursor: 'pointer',
          lineHeight: 1,
          padding: 0,
        }}
      >
        ×
      </button>
    </div>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const navType = useNavigationType();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, userData, loading: authLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSplash, setShowSplash] = useState(false);

  const sessionExpired = searchParams.get('reason') === 'session_expired';
  const dismissBanner = () => {
    searchParams.delete('reason');
    setSearchParams(searchParams, { replace: true });
  };

  useEffect(() => {
    if (authLoading) return;
    // FIX #2 race condition: si user existe pero userData aún no llegó
    // del snapshot, esperar. destinationFor(null) devuelve /dashboard y
    // ProtectedRoute requireAccess+hasAccess(null)=false manda a /paywall
    // antes de que el snap defina rol.
    if (user && !userData) return;
    // FIX #3 back-button: si llegamos acá vía POP (back/forward del
    // browser, ej: /signup → /terminos → back), no auto-redirigir; el
    // usuario decidió volver al form. Solo redirigimos en navegación
    // PUSH/REPLACE (post-login fresh, o entrar a /login con sesión activa
    // por primera vez).
    if (navType === 'POP') return;
    if (user && !showSplash) {
      // FASE UX: handleSubmit/handleGoogle ya activan splash al click.
      // Este branch cubre el caso "sesión activa al entrar a /login"
      // (cerró tab y volvió). Sin showSplash guard, no es un bug pero
      // evitamos un setState innecesario.
      setShowSplash(true);
    }
  }, [user, userData, authLoading, navType, showSplash]);

  const handleSplashComplete = () => {
    // ISSUE 7: respetar `from` si es ruta safe del cliente, sino fallback
    // al destino por rol. SAFE_PATHS depende del rol del user — un cliente
    // nunca puede caer en /admin/dashboard via state.from.
    const fromPath = location.state?.from?.pathname;
    const isAdminUser = userData?.role === 'admin';
    const safePaths = isAdminUser ? ADMIN_SAFE_PATHS : CLIENT_SAFE_PATHS;
    const dest = (fromPath && safePaths.test(fromPath))
      ? fromPath
      : destinationFor(userData);
    navigate(dest, { replace: true });
  };

  const handleSubmit = async () => {
    if (loading) return;
    setError('');
    setLoading(true);
    // FIX parpadeo splash: el splash NO se activa antes del await. Si
    // las credenciales son incorrectas o falla validación Firebase,
    // antes veíamos splash → form-con-error (parpadeo feo). Ahora el
    // botón muestra loading state durante la validación; el splash
    // solo aparece una vez que loginWithEmail resolvió OK.
    try {
      await loginWithEmail(email.trim(), password);
      setShowSplash(true);
    } catch (err) {
      setError(authErrorMessage(err));
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    if (loading) return;
    setError('');
    setLoading(true);
    // Splash POST-await: aceptamos 1-2 frames de form visible entre
    // cierre del popup y el setShowSplash. La alternativa (splash
    // pre-await) hacía que el gradiente naranja del splash quedara
    // visible DETRÁS del popup blanco de Google — más jarring que el
    // flash post-popup. El popup de Google es reconocible y breve;
    // el usuario está enfocado en seleccionar cuenta, no en el fondo.
    try {
      await loginWithGoogle();
      setShowSplash(true);
      // navigate lo hace handleSplashComplete cuando termina el splash.
    } catch (err) {
      // Cancelar popup también cae acá (auth/popup-closed-by-user).
      setError(authErrorMessage(err));
      setLoading(false);
    }
  };

  // FIX flash: early return del splash para que React desmonte el form
  // del Login antes de renderear el splash. Sin esto, el portal del
  // splash se montaba en paralelo al form, dejando 1-2 frames con el
  // form visible debajo (notable en mobile).
  if (showSplash) {
    return <SplashScreen type="login" onComplete={handleSplashComplete} />;
  }

  return (
    <div className="mobile-root">
      <section className="screen" style={{ paddingTop: 28, paddingBottom: 40 }}>
        <AuthBack onClick={() => navigate('/')} />
        <AuthLogo />
        <div className="screen-head">
          <h2>
            Bienvenido de vuelta{' '}
            <span style={{ display: 'inline-block', animation: 'ee-wave 2s ease infinite', transformOrigin: '70% 70%' }}>
              👋
            </span>
          </h2>
          <p>Ingresa para ver tus picks del día.</p>
        </div>

        <GoogleBtn onClick={handleGoogle} disabled={loading}>
          Iniciar sesión con Google
        </GoogleBtn>
        <Divider text="o con email" />

        <ErrorBanner msg={error} />

        {sessionExpired && <SessionExpiredBanner onDismiss={dismissBanner} />}

        <div className="input-group">
          <Field
            label="Email"
            type="email"
            placeholder="tu@email.com"
            value={email}
            onChange={setEmail}
            autoComplete="email"
          />
          <Field
            label="Contraseña"
            type="password"
            placeholder="Tu contraseña"
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            right={
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            }
          />
        </div>

        <div style={{ textAlign: 'right', marginTop: 12 }}>
          <a
            onClick={() => navigate('/recuperar')}
            style={{ fontSize: 13, color: 'var(--ink)', fontWeight: 500, textDecoration: 'underline', cursor: 'pointer' }}
          >
            ¿Olvidaste tu contraseña?
          </a>
        </div>

        <button
          className="btn-primary accent"
          style={{ marginTop: 22, opacity: loading ? 0.5 : 1, pointerEvents: loading ? 'none' : 'auto' }}
          onClick={handleSubmit}
        >
          {loading ? 'Entrando...' : 'Iniciar sesión'}
        </button>

        <div style={{ textAlign: 'center', marginTop: 22, fontSize: 13, color: 'var(--muted)' }}>
          ¿No tienes cuenta?{' '}
          <a
            onClick={() => navigate('/signup')}
            style={{ color: 'var(--ink)', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}
          >
            Crea una
          </a>
        </div>
      </section>
    </div>
  );
}
